import { useState, useEffect } from 'react';
import api, { SubscriptionPlan } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface UseSubscriptionPlanResult {
  plan: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
  hasAds: boolean;
  isPremium: boolean;
  isFreePlan: boolean;
}

export const useSubscriptionPlan = (): UseSubscriptionPlanResult => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserPlan = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/subscription');
        const userPlan = response.data.subscription?.plan ?? response.data.plan;
        setPlan(userPlan ?? null);
        setError(null);
      } catch (err) {
        console.error('Error loading subscription plan:', err);
        setError('Impossible de charger le plan d\'abonnement');
        // En cas d'erreur, on considère que l'utilisateur a le plan gratuit par sécurité
        setPlan({
          id: 0,
          name: 'free_with_ads',
          display_name: 'Plan Gratuit',
          description: 'Plan gratuit avec publicités',
          price_monthly: 0,
          price_yearly: 0,
          has_ads: true,
          features: [],
          max_boards: null,
          max_cards_total: null,
          max_columns_per_board: null,
          max_users: null,
          max_storage_mb: 1024,
          max_guests_per_board: 10,
          price_per_10_extra_boards: 0,
          price_per_10_extra_boards_yearly: 0,
          price_per_1000_extra_cards: 0,
          price_per_1000_extra_cards_yearly: 0,
          price_per_extra_gb: 0,
          price_per_extra_gb_yearly: 0,
          price_per_editor_monthly: 0,
          price_per_editor_yearly: 0,
          is_active: true,
          is_admin_only: false,
          sort_order: 1,
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserPlan();
  }, [user]);

  return {
    plan,
    loading,
    error,
    hasAds: plan?.has_ads ?? true, // Par défaut, on affiche les pubs
    isPremium: plan ? !plan.has_ads : false,
    isFreePlan: plan?.has_ads ?? true,
  };
};
